import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  validatePAN,
  validateGST,
  validateMobile,
  validateEmail,
  validateIFSC,
  validateAccountNumber,
  validateAadhar,
} from "../../../../utils/validationUtils";
import {
  toDateInput,
  validateAgreementStartDate,
  validateAgreementEndDate,
} from "../../../../utils/dateUtils";
import { fetchPincodeDetailsAction } from "../../../../actions/pincodeAction";
import { updateEntityAction } from "../../../../actions/updateEntityAction";
import { fetchCompanyChildAction } from "../../../../actions/fetchCompanyChildAction";

// ── Department options — mirrors ClientFields.js AA_DEPT_MAP ──────────────────
const DEPT_BASE = [{ value: "", label: "Select department function" }];
const DEPT_SALES = [...DEPT_BASE, { value: "Sales", label: "Sales" }];
const DEPT_OPS = [
  ...DEPT_BASE,
  { value: "Purchase", label: "Purchase" },
  { value: "Marketing", label: "Marketing" },
  { value: "Operation", label: "Operation" },
];
const DEPT_CORP = [
  ...DEPT_BASE,
  { value: "HR", label: "Human Resources" },
  { value: "IT", label: "Information Technology" },
  { value: "Finance", label: "Finance" },
];
const AA_DEPT_MAP = {
  client: {
    travel: DEPT_SALES,
    auto: DEPT_SALES,
    gadget: DEPT_SALES,
    health: DEPT_SALES,
  },
  "service partner": {
    travel: DEPT_OPS,
    auto: DEPT_OPS,
    gadget: DEPT_OPS,
    travel_service: DEPT_OPS,
    auto_service: DEPT_OPS,
    gadget_service: DEPT_OPS,
    mobile_service: DEPT_OPS,
    corporate: DEPT_CORP,
  },
  corporate: { corporate: DEPT_CORP },
};

const getDeptOptions = (entityCategory, entityLOBType) => {
  const cat = (entityCategory || "").toLowerCase().trim();
  const lob = (entityLOBType || "").toLowerCase().trim();
  return AA_DEPT_MAP[cat]?.[lob] || DEPT_BASE;
};

// ── Field validation ───────────────────────────────────────────────────────────
const validateField = (fieldName, value) => {
  if (!value || (typeof value === "string" && value.trim() === "")) return null;
  switch (fieldName) {
    case "panNumber":
    case "panCardNumber":
    case "CompanypanNumber":
      return validatePAN(value) === true ? null : validatePAN(value);
    case "aadharCardNumber":
    case "aadharNumberOfBothPartners":
    case "directorAadharNumber":
    case "aadharNumberOfTrustee":
      return validateAadhar(value) === true ? null : validateAadhar(value);
    case "gstNumber":
      return validateGST(value) === true ? null : validateGST(value);
    case "mobileNumber":
      return validateMobile(value) === true ? null : validateMobile(value);
    case "emailAddress":
      return validateEmail(value) === true ? null : validateEmail(value);
    case "ifscCode":
      return validateIFSC(value) === true ? null : validateIFSC(value);
    case "accountNumber":
      return validateAccountNumber(value) === true
        ? null
        : validateAccountNumber(value);
    default:
      return null;
  }
};

// ── Normalizers ────────────────────────────────────────────────────────────────
const normalizeEntityType = (value) => {
  if (!value) return "";
  const m = {
    proprietorship: "proprietorship",
    partnership: "partnership",
    llp: "llp",
    private_limited: "private_limited",
    public_limited: "public_limited",
    trust: "trust",
    society: "society",
    client: "",
    vendor: "",
  };
  return m[value.toLowerCase().trim()] ?? value;
};

const normalizeLOBType = (value) => {
  if (!value) return "";
  const m = {
    travel: "travel",
    company: "company",
    auto: "auto",
    gadget: "gadget",
    "non-travel": "company",
    nontravel: "company",
  };
  return m[value.toLowerCase().trim()] ?? value;
};

const normalizeLOBNationality = (value) => {
  if (!value) return "";
  const m = { domestic: "Domestic", international: "international" };
  return m[value.toLowerCase().trim()] ?? value;
};

// ── Hook ───────────────────────────────────────────────────────────────────────
export const useEditDetails = (clientData, onSave, onClose) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Parent company options — fetched from API (same source as ParentCode page)
  const [parentOptions, setParentOptions] = useState([
    { value: "", label: "Select company" },
  ]);
  useEffect(() => {
    fetchCompanyChildAction().then((result) => {
      if (result.success && result.data.length > 0) {
        setParentOptions([
          { value: "", label: "Select company" },
          ...result.data.map((r) => ({
            value: String(r.id),
            label: r.parentName || `Company ${r.id}`,
          })),
        ]);
      }
    });
  }, []);

  // Department options — reactive to current form values (category + LOB)
  const departmentOptions = getDeptOptions(
    formData.entityCategory || clientData?.entity?.entityCategory,
    formData.entityLOBType ||
      formData.lobType ||
      clientData?.entity?.entityLOBType,
  );

  // Seed form when clientData changes
  useEffect(() => {
    if (!clientData?.entity) return;
    const e = clientData.entity;
    const rawDept = e.aaDepartmentFunction || "";
    console.log(
      "[EditModal] dept raw:",
      rawDept,
      "| options:",
      departmentOptions.map((o) => o.value),
    );
    setFormData({
      ...e,
      entityType: normalizeEntityType(e.entityType),
      lobType: normalizeLOBType(e.lobType),
      lobNationality: normalizeLOBNationality(e.lobNationality),
      aaDepartmentFunction: rawDept, // API value matches option value directly
      parentCode: e.parentId ? String(e.parentId) : "",
      agreementStartDate: toDateInput(e.agreementStartDate),
      agreementEndDate: toDateInput(e.agreementEndDate),
    });
  }, [clientData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── File states for KYC documents ────────────────────────────────────────
  const [utilityFile, setUtilityFile] = useState(null);
  const [msmeFile, setMsmeFile] = useState(null);
  const [thirdPartyFile, setThirdPartyFile] = useState(null);

  // Beneficiary vs trade name match — drives ThirdPartySection visibility
  const showThirdParty =
    formData.hasBankDetails === "yes" &&
    (formData.tradeName || "").trim() !== "" &&
    (formData.beneficiaryName || "").trim() !== "" &&
    (formData.tradeName || "").trim().toLowerCase() !==
      (formData.beneficiaryName || "").trim().toLowerCase();
  const pincodeRef = useRef(null);
  useEffect(() => {
    const pin = formData.pinCode
      ? String(formData.pinCode).replace(/\D/g, "")
      : "";
    if (pin.length !== 6 || pincodeRef.current === pin) return;
    pincodeRef.current = pin;
    fetchPincodeDetailsAction(pin).then((result) => {
      if (result.success) {
        setFormData((prev) => ({
          ...prev,
          city: result.city,
          state: result.state,
        }));
      }
    });
  }, [formData.pinCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    const uppercaseFields = [
      "panNumber",
      "companyPAN",
      "gstNumber",
      "ifscCode",
    ];
    if (uppercaseFields.includes(name) && typeof newValue === "string")
      newValue = newValue.toUpperCase();

    if (
      ["mobileNumber", "accountNumber", "pinCode"].includes(name) &&
      typeof newValue === "string"
    ) {
      newValue = newValue
        .replace(/\D/g, "")
        .slice(0, name === "pinCode" ? 6 : undefined);
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (type !== "checkbox") {
      const updatedForm = { ...formData, [name]: newValue };
      const error = validateField(name, newValue);
      setErrors((prev) => {
        const e = { ...prev };
        if (error) e[name] = error;
        else delete e[name];
        if (name === "agreementStartDate" || name === "agreementEndDate") {
          const endErr = validateAgreementEndDate(
            updatedForm.agreementEndDate,
            updatedForm.agreementStartDate,
          );
          const startErr = validateAgreementStartDate(
            updatedForm.agreementStartDate,
            updatedForm.agreementEndDate,
          );
          if (endErr) e.agreementEndDate = endErr;
          else delete e.agreementEndDate;
          if (startErr) e.agreementStartDate = startErr;
          else delete e.agreementStartDate;
        }
        return e;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prev) => {
      const e = { ...prev };
      if (error) {
        e[name] = error;
        return e;
      }
      delete e[name];

      // Cross-check: PAN and Director PAN must not be the same
      const panFields = ["panCardNumber", "CompanypanNumber", "doc_pan_number"];
      const dirPanFields = ["directorPANNumber", "doc_directorPan_number"];
      const isPan = panFields.includes(name);
      const isDirPan = dirPanFields.includes(name);

      if (isPan || isDirPan) {
        const panVal = (
          isPan
            ? value
            : formData.panCardNumber || formData.CompanypanNumber || ""
        )
          .trim()
          .toUpperCase();
        const dirPanVal = (isDirPan ? value : formData.directorPANNumber || "")
          .trim()
          .toUpperCase();

        if (panVal && dirPanVal && panVal === dirPanVal) {
          if (isPan) e[name] = "PAN Card and Director PAN must not be the same";
          if (isDirPan)
            e[name] = "Director PAN must be different from PAN Card";
        } else {
          // Clear stale cross-check errors
          [...panFields, ...dirPanFields].forEach((f) => {
            if (
              e[f] === "PAN Card and Director PAN must not be the same" ||
              e[f] === "Director PAN must be different from PAN Card"
            ) {
              delete e[f];
            }
          });
        }
      }

      return e;
    });
  };

  const handleUpdate = async (e, uploadedPaths = {}) => {
    e.preventDefault();
    const startErr = validateAgreementStartDate(
      formData.agreementStartDate,
      formData.agreementEndDate,
    );
    const endErr = validateAgreementEndDate(
      formData.agreementEndDate,
      formData.agreementStartDate,
    );
    if (startErr || endErr) {
      setErrors((prev) => ({
        ...prev,
        ...(startErr ? { agreementStartDate: startErr } : {}),
        ...(endErr ? { agreementEndDate: endErr } : {}),
      }));
      return;
    }
    setIsSubmitting(true);
    try {
      // Merge newly uploaded paths over existing ones — new upload wins
      const mergedData = {
        ...formData,
        ...(uploadedPaths.UtilityBillDocumentPath && {
          utilityBillDocumentPath: uploadedPaths.UtilityBillDocumentPath,
        }),
        ...(uploadedPaths.MSMECertificatePath && {
          msmeCertificatePath: uploadedPaths.MSMECertificatePath,
        }),
        ...(uploadedPaths.UploadSignedFormPath && {
          uploadSignedFormPath: uploadedPaths.UploadSignedFormPath,
        }),
        ...(uploadedPaths.PassbookOrCancelledChequePath && {
          passbookOrCancelledChequePath:
            uploadedPaths.PassbookOrCancelledChequePath,
        }),
        ...(uploadedPaths.AadharCardPath && {
          aadharCardPath: uploadedPaths.AadharCardPath,
        }),
        ...(uploadedPaths.PANCardPath && {
          panCardPath: uploadedPaths.PANCardPath,
        }),
        ...(uploadedPaths.DirectorPANPath && {
          directorPANPath: uploadedPaths.DirectorPANPath,
        }),
        ...(uploadedPaths.CertificateOfIncorporationPath && {
          certificateOfIncorporationPath:
            uploadedPaths.CertificateOfIncorporationPath,
        }),
        ...(uploadedPaths.PartnershipDeedPath && {
          partnershipDeedPath: uploadedPaths.PartnershipDeedPath,
        }),
        ...(uploadedPaths.LLPCertificatePath && {
          uploadLLPCertificatePath: uploadedPaths.LLPCertificatePath,
        }),
      };
      const result = await updateEntityAction(mergedData.id, mergedData);
      if (result.success) {
        toast.success("Entity updated successfully!");
        onClose();
      } else {
        toast.error(result.message || "Failed to update entity");
      }
    } catch {
      toast.error("Failed to update entity");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  const allDocsUploaded = (() => {
    const docs = clientData?.documents || [];
    if (docs.length === 0) return false;
    return docs.every((doc) => doc.files?.some((f) => f.isUploaded));
  })();

  const getDocumentStatusBadge = (isUploaded) => {
    if (isUploaded) return null;
    return <span className="doc-status pending">⏳ Pending</span>;
  };

  const getFieldValidationState = (fieldName) => {
    const value = formData[fieldName];
    const error = errors[fieldName];
    const hasValue = value && value.toString().trim() !== "";
    const hasValidation = [
      "panNumber",
      "panCardNumber",
      "CompanypanNumber",
      "aadharCardNumber",
      "aadharNumberOfBothPartners",
      "directorAadharNumber",
      "aadharNumberOfTrustee",
      "gstNumber",
      "mobileNumber",
      "emailAddress",
      "ifscCode",
      "accountNumber",
    ].includes(fieldName);
    return {
      error,
      isValid: hasValue && !error && hasValidation,
      className: error
        ? "error"
        : hasValue && !error && hasValidation
          ? "success"
          : "",
    };
  };

  return {
    formData,
    errors,
    isSubmitting,
    departmentOptions,
    parentOptions,
    utilityFile,
    setUtilityFile,
    msmeFile,
    setMsmeFile,
    thirdPartyFile,
    setThirdPartyFile,
    showThirdParty,
    handleChange,
    handleBlur,
    handleUpdate,
    handleClose,
    getDocumentStatusBadge,
    getFieldValidationState,
    allDocsUploaded,
  };
};
